import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { employees as employeesApi } from '../../api/organization';
import { ApiError } from '../../api/problem';
import { projects as projectsApi, repositories as repositoriesApi } from '../../api/projects';
import { tasks, type CreateTaskBody, type TaskPriority, type TaskType } from '../../api/tasks';
import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { NumberField, SelectField, SwitchField, TagsField, TextField } from '../../components/form/Inputs';
import { PageHeader } from '../../components/Surface';
import { useT } from '../../i18n/provider';
import type { Key } from '../../i18n/provider';
import { useCompanyId, useCompanySlug } from '../company/useCompany';

const TASK_TYPES: TaskType[] = [
  'feature',
  'bug',
  'refactor',
  'test',
  'docs',
  'chore',
  'security',
  'research',
  'new_project',
  'review',
  'ops',
];

const PRIORITIES: TaskPriority[] = ['low', 'normal', 'high', 'urgent'];

/**
 * El `/tasks/new` de `13 §2`.
 *
 * La descripción se lleva el espacio y la explicación, porque es lo que lee el
 * agente: una tarea bien especificada es lo más barato que un gerente le puede
 * dar a un agente (`12 §8`). `auto_queue` decide si la tarea nace como `draft`
 * o va directa a la cola del orquestador.
 */
export function NewTaskScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const companySlug = useCompanySlug();
  const navigate = useNavigate();
  // `?project=` es el slug de la pestaña desde la que se pulsó «Nueva tarea».
  const { project: requestedProject, type: requestedType, assignee: requestedAssignee, queue: requestedQueue } = useSearch({ from: '/authenticated/c/$companySlug/tasks/new' });

  const [projectId, setProjectId] = useState('');
  const [repositoryIds, setRepositoryIds] = useState<string[]>([]);
  const [type, setType] = useState<TaskType>(requestedType ?? 'feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [acceptance, setAcceptance] = useState('');
  const [priority, setPriority] = useState<TaskPriority>('normal');
  const [assigneeId, setAssigneeId] = useState(requestedAssignee ?? '');
  const [budgetUsd, setBudgetUsd] = useState(0);
  const [labels, setLabels] = useState<string[]>([]);
  const [autoQueue, setAutoQueue] = useState(requestedQueue === '1');
  const [teamMeeting, setTeamMeeting] = useState(false);
  // Encendido por defecto (`09 §2.2`): solo actúa si el proyecto tiene Jenkins.
  const [awaitDeploy, setAwaitDeploy] = useState(true);

  const projectList = useQuery({
    queryKey: ['projects', companyId],
    queryFn: () => projectsApi.list(companyId),
    enabled: companyId !== '',
  });

  // Preseleccionar el proyecto de la pestaña una sola vez, cuando la lista
  // llega; si la persona ya eligió otro mientras cargaba, se respeta.
  const requestedProjectId = projectList.data?.data.find((project) => project.slug === requestedProject)?.id;
  useEffect(() => {
    if (requestedProjectId !== undefined) {
      setProjectId((current) => (current === '' ? requestedProjectId : current));
    }
  }, [requestedProjectId]);

  const employeeList = useQuery({
    queryKey: ['employees', companyId],
    queryFn: () => employeesApi.list(companyId),
    enabled: companyId !== '',
  });

  const repositoryList = useQuery({
    queryKey: ['repositories', projectId],
    queryFn: () => repositoriesApi.list(projectId, false),
    enabled: projectId !== '',
  });

  const create = useMutation({
    mutationFn: (body: CreateTaskBody) => tasks.create(companyId, body),
    onSuccess: (task) =>
      void navigate({
        to: '/c/$companySlug/tasks/$taskNumber',
        params: { companySlug, taskNumber: String(task.number) },
      }),
  });

  const fields = create.error instanceof ApiError ? create.error.fields : {};
  // Sin proyecto no hay candidatos (`09 §3`): encolar exige decir quién.
  const assigneeMissing = autoQueue && projectId === '' && assigneeId === '';

  const submit = () => {
    create.mutate({
      project_id: projectId === '' ? null : projectId,
      type,
      title,
      description,
      acceptance_criteria: acceptance === '' ? null : acceptance,
      priority,
      repository_ids: repositoryIds,
      assignee_employee_id: assigneeId === '' ? null : assigneeId,
      budget_limit_usd: budgetUsd > 0 ? budgetUsd : null,
      labels,
      auto_queue: autoQueue,
      team_meeting: teamMeeting,
      await_deploy: awaitDeploy,
    });
  };

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title={t('newTask.title')} description={t('newTask.intro')} />

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label={t('newTask.type')}
          value={type}
          options={TASK_TYPES.map((value) => ({ value, label: t(`tasks.type.${value}` as Key) }))}
          onChange={(value) => setType(value as TaskType)}
        />
        <SelectField
          label={t('newTask.priority')}
          value={priority}
          options={PRIORITIES.map((value) => ({ value, label: t(`tasks.priority.${value}` as Key) }))}
          onChange={(value) => setPriority(value as TaskPriority)}
        />
      </div>

      <TextField label={t('newTask.taskTitle')} value={title} onChange={setTitle} maxLength={200} error={fields['title']} />
      <TextField
        label={t('newTask.description')}
        value={description}
        onChange={setDescription}
        rows={6}
        error={fields['description']}
        hint={t('newTask.descriptionHint')}
      />
      <TextField
        label={t('newTask.acceptance')}
        value={acceptance}
        onChange={setAcceptance}
        rows={3}
        error={fields['acceptance_criteria']}
        hint={t('newTask.acceptanceHint')}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          label={t('newTask.project')}
          value={projectId}
          options={[
            { value: '', label: t('common.none') },
            ...(projectList.data?.data ?? []).map((project) => ({ value: project.id, label: project.name })),
          ]}
          onChange={(value) => {
            setProjectId(value);
            setRepositoryIds([]);
          }}
        />
        <SelectField
          label={t('newTask.assignee')}
          value={assigneeId}
          options={[
            { value: '', label: t('newTask.letRouterDecide') },
            ...(employeeList.data?.data ?? []).map((employee) => ({
              value: employee.id,
              label: `${employee.name} · ${employee.job_title}`,
            })),
          ]}
          onChange={setAssigneeId}
          error={fields['assignee_employee_id']}
          hint={assigneeMissing ? t('newTask.assigneeRequiredWithoutProject') : undefined}
        />
      </div>

      {projectId !== '' && (repositoryList.data?.data ?? []).length > 0 ? (
        <fieldset className="space-y-2">
          <legend className="text-sm font-semibold text-ink">{t('newTask.repositories')}</legend>
          {(repositoryList.data?.data ?? []).map((repository) => (
            <label key={repository.id} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={repositoryIds.includes(repository.id)}
                onChange={(event) =>
                  setRepositoryIds((current) =>
                    event.target.checked
                      ? [...current, repository.id]
                      : current.filter((id) => id !== repository.id),
                  )
                }
              />
              <span className="font-mono text-xs">{repository.full_name}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <NumberField
          label={t('newTask.budgetLimit')}
          value={budgetUsd}
          onChange={setBudgetUsd}
          min={0}
          hint={t('newTask.budgetHint')}
        />
        <TagsField
          label={t('newTask.labels')}
          values={labels}
          onChange={setLabels}
          hint={t('newTask.labelsHint')}
        />
      </div>

      <SwitchField
        label={t('newTask.awaitDeploy')}
        checked={awaitDeploy}
        onChange={setAwaitDeploy}
        hint={t('newTask.awaitDeployHint')}
      />

      <SwitchField
        label={t('newTask.teamMeeting')}
        checked={teamMeeting}
        onChange={setTeamMeeting}
        hint={t('newTask.teamMeetingHint')}
      />

      <SwitchField
        label={t('newTask.queueNow')}
        checked={autoQueue}
        onChange={setAutoQueue}
        hint={t('newTask.queueHint')}
      />

      {create.error !== null ? <ErrorBanner error={create.error} /> : null}

      <Button size="lg" busy={create.isPending} onClick={submit} disabled={title === '' || description === '' || assigneeMissing}>
        {autoQueue ? t('newTask.createAndQueue') : t('newTask.createDraft')}
      </Button>
    </div>
  );
}
